import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import userManager from '../services/userManager';
import { API_BASE_URL } from '../services/httpClient';
import { ArrowLeft } from 'lucide-react';

/**
 * Daily Tinnitus Check-in Screen
 * 
 * Simple screen asking: "How are your tinnitus today?"
 * User selects a score 1-10 via emoticons
 * Score is submitted to /api/impact/today
 * Then redirects to /progress
 */

const EMOTICONS = [
  { score: 1, emoji: '😊', label_fr: 'Très bien', label_en: 'Very good' },
  { score: 2, emoji: '🙂', label_fr: 'Bien', label_en: 'Good' },
  { score: 3, emoji: '😌', label_fr: 'Correct', label_en: 'Okay' },
  { score: 4, emoji: '😐', label_fr: 'Moyen', label_en: 'Average' },
  { score: 5, emoji: '😕', label_fr: 'Pas top', label_en: 'Not great' },
  { score: 6, emoji: '😟', label_fr: 'Gênant', label_en: 'Bothersome' },
  { score: 7, emoji: '😣', label_fr: 'Difficile', label_en: 'Difficult' },
  { score: 8, emoji: '😫', label_fr: 'Pénible', label_en: 'Hard' },
  { score: 9, emoji: '😩', label_fr: 'Très dur', label_en: 'Very hard' },
  { score: 10, emoji: '😵', label_fr: 'Insupportable', label_en: 'Unbearable' },
];

const CoachCheckin = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedScore, setSelectedScore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const lang = localStorage.getItem('hearen_lang') || user?.language || 'fr';
  const isFr = lang === 'fr';

  const handleSubmit = async () => {
    if (!selectedScore) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      const userId = await userManager.getUserId();
      
      const response = await fetch(
        `${API_BASE_URL}/api/impact/today?user_id=${userId}&score=${selectedScore}`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to submit score');
      }
      
      // Success - redirect to progress page
      navigate('/progress', { replace: true });
      
    } catch (err) {
      console.error('Error submitting check-in:', err);
      setError(isFr ? 'Erreur lors de la soumission' : 'Error submitting');
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/coach');
  };

  return (
    <div 
      className="min-h-screen pb-32 relative overflow-hidden"
      style={{ background: '#0a0a12' }}
    >
      {/* Subtle cosmic background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(88, 28, 135, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(59, 72, 238, 0.08) 0%, transparent 50%)
          `,
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-lg mx-auto px-5 pt-8">
        
        {/* Back button */}
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">{isFr ? 'Retour' : 'Back'}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 border border-white/10 mb-4">
            <span className="text-3xl">👂</span>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">
            {isFr 
              ? 'Comment vont vos acouphènes aujourd\'hui ?' 
              : 'How are your tinnitus today?'}
          </h1>
          <p className="text-white/60 text-sm">
            {isFr 
              ? 'Choisissez une note. Vous pourrez la modifier plus tard.'
              : 'Pick a rating. You can change it later.'}
          </p>
        </div>

        {/* Emoticon Grid */}
        <div className="grid grid-cols-5 gap-3 mb-8">
          {EMOTICONS.map((item) => (
            <button
              key={item.score}
              onClick={() => setSelectedScore(item.score)}
              className={`
                flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-200
                ${selectedScore === item.score
                  ? 'bg-violet-500/30 border-2 border-violet-400 scale-105 shadow-lg shadow-violet-500/20'
                  : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
                }
              `}
            >
              <span className="text-3xl mb-1">{item.emoji}</span>
              <span className="text-xs text-white/70 font-medium">{item.score}</span>
            </button>
          ))}
        </div>

        {/* Selected score label */}
        {selectedScore && (
          <div className="text-center mb-6 animate-in fade-in duration-200">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <span className="text-xl">{EMOTICONS[selectedScore - 1].emoji}</span>
              <span className="text-white/80 font-medium">
                {isFr ? EMOTICONS[selectedScore - 1].label_fr : EMOTICONS[selectedScore - 1].label_en}
              </span>
              <span className="text-white/50">({selectedScore}/10)</span>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-sm text-center">
            {error}
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={!selectedScore || submitting}
          className={`
            w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200
            ${selectedScore && !submitting
              ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-500/25'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
            }
          `}
        >
          {submitting 
            ? (isFr ? 'Envoi...' : 'Submitting...')
            : (isFr ? 'Valider' : 'Confirm')
          }
        </button>

        {/* Skip option */}
        <button
          onClick={() => navigate('/progress', { replace: true })}
          className="w-full mt-4 py-3 text-white/50 hover:text-white/70 text-sm transition-colors"
        >
          {isFr ? 'Passer pour l\'instant' : 'Skip for now'}
        </button>

      </div>
    </div>
  );
};

export default CoachCheckin;
